/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { Breadcrumb, Button, Col, Row, Spin, Typography } from 'antd';
import { PlusOutlined, ScheduleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import MainLayout from '../components/common/MainLayout';
import HomeHeader from '../components/home/HomeHeader';
import MyPlotCard from '../components/home/MyPlotCard';
import PlanningCard from '../components/home/PlanningCard';
import HomeMiniCard from '../components/home/HomeMiniCard';
import { useListSavesQuery } from '../api/savesV2Api';
import { selectIsAuthenticated } from '../store/slices/authSlice';
import learnArt from '../assets/home/learn.webp';
import challengesArt from '../assets/home/challenges.webp';
import seasonsArt from '../assets/home/seasons.webp';

const { Paragraph } = Typography;

const styles = {
  page: css({ display: 'flex', flexDirection: 'column', gap: 16 }),
  miniGrid: css({ marginTop: 8 }),
  emptyFarmCta: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'flex-start',
    padding: 16,
  }),
};

export default function HomePage() {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const { data: freeSaves, isLoading: savesLoading } = useListSavesQuery(
    { type: 'free', status: 'active', limit: 20 },
    { skip: !isAuthenticated },
  );
  const freeSave = useMemo(
    () => (freeSaves || [])[0] || null,
    [freeSaves],
  );

  const openSavePath = async (path: (saveId: string) => string) => {
    if (freeSave) {
      navigate(path(freeSave.id));
      return;
    }
    navigate('/saves?type=free');
  };

  const goToSaveRoot = () => openSavePath((id) => `/saves/${id}`);
  const goToTasks = () => openSavePath((id) => `/saves/${id}/tasks`);
  const goToPlannerHub = () => openSavePath((id) => `/saves/${id}`);

  if (!isAuthenticated) {
    return null;
  }

  if (savesLoading) {
    return (
      <MainLayout breadcrumb={<Breadcrumb items={[{ title: t('breadcrumbs.home') }]} />}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  const hasSave = Boolean(freeSave);

  return (
    <MainLayout breadcrumb={<Breadcrumb items={[{ title: t('breadcrumbs.home') }]} />}>
      <div css={styles.page}>
        <HomeHeader />

        {hasSave ? (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={14}>
                <MyPlotCard
                  farmName={freeSave?.farmName || t('home.myPlot')}
                  modeLabel={t('home.freeMode')}
                  modeDescription={t('home.freeModeDescription')}
                  hasFarm={hasSave}
                  hasSave={hasSave}
                  loading={savesLoading}
                  onPrimaryClick={goToSaveRoot}
                  primaryLabel={hasSave ? t('home.openPlot') : t('home.play')}
                  primaryLoading={false}
                  actions={[
                    {
                      key: 'water',
                      icon: <ScheduleOutlined />,
                      title: t('home.quickActions.watering.title'),
                      hint: t('home.quickActions.watering.hint'),
                      onClick: goToTasks,
                    },
                    {
                      key: 'harvest',
                      icon: <ScheduleOutlined />,
                      title: t('home.quickActions.harvest.title'),
                      hint: t('home.quickActions.harvest.hint'),
                      onClick: goToTasks,
                    },
                    {
                      key: 'greenhouse',
                      icon: <ScheduleOutlined />,
                      title: t('home.quickActions.greenhouse.title'),
                      hint: t('home.quickActions.greenhouse.hint'),
                      onClick: goToSaveRoot,
                    },
                  ]}
                />
              </Col>
              <Col xs={24} lg={10}>
                <PlanningCard
                  description={t('home.planning.description')}
                  onOpen={goToPlannerHub}
                  ctaLabel={hasSave ? t('home.planning.openPlans') : t('home.planning.playAndPlan')}
                  loading={false}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]} css={styles.miniGrid}>
              <Col xs={24} sm={12} md={8}>
                <HomeMiniCard
                  tone="cream"
                  art={learnArt}
                  title={t('home.cards.learning.title')}
                  description={t('home.cards.learning.description')}
                  badge={t('home.badges.soon')}
                  disabled
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <HomeMiniCard
                  tone="lilac"
                  art={challengesArt}
                  title={t('home.cards.challenges.title')}
                  description={t('home.cards.challenges.description')}
                  badge={t('home.badges.free')}
                  onClick={() => navigate('/scenarios')}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <HomeMiniCard
                  tone="pink"
                  art={seasonsArt}
                  title={t('home.cards.seasons.title')}
                  description={t('home.cards.seasons.description')}
                  badge={t('home.badges.soon')}
                  disabled
                />
              </Col>
            </Row>
          </>
        ) : (
          <div css={styles.emptyFarmCta}>
            <Paragraph>
              {t('home.welcome')}
            </Paragraph>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => navigate('/saves?type=free')}
            >
              {t('home.createFirstSave')}
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
